import React from 'react';
import { Building2, MapPin, Calendar, Users, Navigation, Palette, Star, Shield, Briefcase } from 'lucide-react';

const CompanyOverview = () => {
  const companyData = {
    name: '株式会社セイライグループジャパン',
    nameEn: 'SEIRAI GROUP JAPAN Co., Ltd.',
    address: '〒102-0071 東京都千代田区富士見2-10-2',
    addressLine2: '飯田橋グラン・ブルーム 9F',
    established: '2019年8月1日',
    representative: 'シリカン アサワ',
    properties: '14物件',
  };

  // 3 Core Values based on PDF content
  const coreValues = [
    {
      icon: Navigation,
      titleEn: 'LOCATION',
      title: 'ロケーション',
      description: '家族にやさしいエリアを厳選。主要観光エリアへのアクセスが良く、夜は静かで安心して歩ける環境。最寄り駅から徒歩圏内、スーパー・コンビニも近くにある生活利便性を重視。',
      features: [
        '主要観光エリアへのアクセス良好',
        '静かで治安の良いエリア',
        'ベビーカーでも移動しやすい導線',
        '生活利便施設が徒歩圏内'
      ],
      gradient: 'from-emerald-500 to-teal-600',
      bgGradient: 'from-emerald-50 to-teal-50',
    },
    {
      icon: Palette,
      titleEn: 'DESIGN',
      title: 'デザイン',
      description: 'ミニマルな和モダンと各エリアのローカル要素を融合した「フュージョンコンセプト」。家族が自然と集まる「ボンディングエリア」を中心に、本物感と居心地の良さを両立した空間設計。',
      features: [
        '考え抜かれた機能性: 直感的な動線・効率的なレイアウト',
        'プレミアム設備・高品質な家電とアメニティ',
        '子どもと高齢者への安全配慮',
        'プライバシーと静けさを重視'
      ],
      gradient: 'from-amber-500 to-orange-600',
      bgGradient: 'from-amber-50 to-orange-50',
    },
    {
      icon: Star,
      titleEn: 'REVIEW',
      title: 'レビュー',
      description: '「基本の徹底」が生み出す圧倒的なゲスト満足度。清掃品質の自社コントロール、5分以内のメッセージ返信、24時間コンシェルジュサービスで、市場平均を大きく上回る評価を獲得。',
      features: [
        '総合評価 4.96/5（Airbnb）',
        '総合品質スコア 96.9%（市場平均85.1%）',
        'Booking.com 平均スコア 9.2',
        'メッセージ平均返信時間 約4分'
      ],
      gradient: 'from-violet-500 to-purple-600',
      bgGradient: 'from-violet-50 to-purple-50',
    },
  ];

  const infoItems = [
    {
      icon: Building2,
      label: '会社名',
      value: companyData.name,
      subValue: companyData.nameEn,
    },
    {
      icon: MapPin,
      label: '所在地',
      value: companyData.address,
      subValue: companyData.addressLine2,
    },
    {
      icon: Calendar,
      label: '設立',
      value: companyData.established,
    },
    {
      icon: Users,
      label: '代表',
      value: companyData.representative,
    },
    {
      icon: Briefcase,
      label: '事業内容',
      value: '民泊・旅館業運営',
      value2: 'ホテルの企画、運営代行',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-stone-50 to-stone-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-light text-stone-800 mb-4 tracking-tight">
            会社概要
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mb-6"></div>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            東京発のライフスタイル型宿泊ブランド「ACOE」を運営
          </p>
        </div>

        {/* Main Content - Centered Single Column */}
        <div className="max-w-2xl mx-auto mb-16">
          {/* Company Info Cards */}
          <div className="space-y-4 mb-8">
            {infoItems.map((item, index) => (
              <div
                key={index}
                className="flex gap-4 p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-amber-700" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-sm text-stone-500 mb-1">{item.label}</div>
                  <div className="text-stone-800 font-medium">{item.value}</div>
                  {item.value2 && (
                    <div className="text-stone-800 font-medium">{item.value2}</div>
                  )}
                  {item.subValue && (
                    <div className="text-sm text-stone-600 mt-1">{item.subValue}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Brand ACOE - Below 代表 */}
          <div className="p-8 bg-gradient-to-br from-amber-50 to-stone-50 rounded-2xl shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Building2 className="w-8 h-8 text-amber-700" />
              <h3 className="text-2xl font-light text-stone-800">ブランド ACOE</h3>
            </div>
            <p className="text-stone-700 leading-relaxed mb-4">
              ファミリー向けに高品質で快適な宿泊体験を提供する、東京発のライフスタイル型宿泊ブランド。
              運営歴は7年以上。
            </p>
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-white p-4 rounded-xl text-center">
                <div className="text-2xl font-semibold text-amber-700">{companyData.properties}</div>
                <div className="text-sm text-stone-600 mt-1">運営物件数</div>
              </div>
              <div className="bg-white p-4 rounded-xl text-center">
                <div className="text-2xl font-semibold text-amber-700">7年+</div>
                <div className="text-sm text-stone-600 mt-1">運営歴</div>
              </div>
            </div>
          </div>
        </div>

        {/* 3 Core Values Section - Below Brand ACOE */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <p className="text-amber-800/80 text-sm tracking-[0.2em] uppercase mb-3">
              Our Competitive Moat
            </p>
            <h3 className="text-2xl md:text-3xl font-light text-stone-800 mb-4">
              ACOEの3つの強み
            </h3>
            <div className="w-16 h-px bg-amber-700/40 mx-auto mb-6" />
            <p className="text-stone-600 max-w-2xl mx-auto">
              この3つの柱が、誰にも真似できない競争優位性を構築し、<br className="hidden md:block" />
              日本で最も愛されるファミリー向け宿泊ブランドへの道を切り開きます
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {coreValues.map((core, index) => (
              <div
                key={index}
                className={`relative rounded-2xl overflow-hidden bg-gradient-to-br ${core.bgGradient} 
                  border border-stone-200/50 shadow-sm hover:shadow-xl transition-all duration-500 group`}
                data-testid={`core-value-${core.titleEn.toLowerCase()}`}
              >
                {/* Header with gradient */}
                <div className={`bg-gradient-to-r ${core.gradient} p-6 text-white`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <core.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white/80 text-xs tracking-[0.15em] uppercase">{core.titleEn}</p>
                      <h4 className="text-xl font-medium">{core.title}</h4>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-stone-700 text-sm leading-relaxed mb-5">
                    {core.description}
                  </p>
                  
                  {/* Features list */}
                  <ul className="space-y-2">
                    {core.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2 text-sm text-stone-600">
                        <Shield className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Decorative corner */}
                <div className="absolute top-0 right-0 w-20 h-20 opacity-10">
                  <div className={`absolute inset-0 bg-gradient-to-br ${core.gradient} rounded-bl-full`} />
                </div>
              </div>
            ))}
          </div>

          {/* Moat statement */}
          <div className="mt-12 text-center">
            <div className="inline-block bg-stone-800 text-white px-8 py-4 rounded-xl shadow-lg">
              <p className="text-lg font-light">
                <span className="text-amber-400">Location × Design × Review</span> = 
                <span className="font-medium ml-2">競争優位性</span>
              </p>
            </div>
          </div>
        </div>

        {/* Mission & Vision - Full Width Focus */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Mission */}
          <div className="p-8 bg-gradient-to-br from-white to-amber-50/30 rounded-2xl shadow-md border border-amber-100/50">
            <h3 className="text-2xl font-light text-stone-800 mb-6 flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-amber-700 font-semibold text-lg">M</span>
              </div>
              ミッション
            </h3>
            <p className="text-stone-700 leading-relaxed text-base">
              家族が共に思い出を作るための空間を提供します。プライベートで清潔、快適な住空間に、温かいサービスとパーソナライズされた体験、最新技術を組み合わせ、ゲストにとってより良く、便利な滞在を実現します。
            </p>
          </div>

          {/* Vision */}
          <div className="p-8 bg-gradient-to-br from-white to-amber-50/30 rounded-2xl shadow-md border border-amber-100/50">
            <h3 className="text-2xl font-light text-stone-800 mb-6 flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-amber-700 font-semibold text-lg">V</span>
              </div>
              ビジョン
            </h3>
            <p className="text-stone-700 leading-relaxed text-base">
              日本で最も愛されるファミリー向けホスピタリティブランドとなり、
              それぞれの家が意図を持って設計され、滞在を通じて家族の絆を深め、
              世代を超えて共有される思い出を創るプラットフォームになります。
            </p>
          </div>
        </div>

        {/* What Makes Us Different */}
        <div className="mt-12 p-8 bg-gradient-to-br from-stone-800 to-stone-700 text-stone-50 rounded-2xl shadow-lg">
          <h3 className="text-2xl font-light mb-6">ホテル運営の現場から</h3>
          
          <div className="space-y-6 text-stone-200 leading-relaxed">
            <p>
              多くの住宅宿泊・宿泊運営の管理会社は、不動産会社を母体としています。背景として、住宅宿泊管理業者の登録にあたり、宅地建物取引業の知見や体制が求められやすい制度環境があるためです。
            </p>
            
            <p>
              一方で、宿泊施設の運営は、長期賃貸の管理とは本質的に異なります。<br />
              「清掃品質」「ゲスト対応」「レビュー設計」「運営導線」「価格調整（レベニュー）」「トラブル時の現場判断」など、日々のオペレーションの積み重ねが収益と資産価値に直結します。
            </p>
            
            <div className="bg-amber-500/20 border border-amber-400/30 rounded-xl p-6 my-6">
              <p className="text-amber-100 font-medium text-lg">
                そのため当社は、単なる管理委託ではなく、<span className="text-amber-400 font-semibold">オーナー様と同じ目線で"運営を磨き、収益を最大化するパートナー"</span>であることを大切にしています。
              </p>
            </div>
            
            <p>
              当社代表は不動産会社出身ではなく、ホテル現場の運営に携わってきました。<br />
              著名ホスピタリティブランドで培った運営ノウハウに加え、需要変動に合わせた高度な収益管理（レベニューマネジメント）を組み合わせることで、高い評価と安定した収益性の両立を目指します。
            </p>
            
            <div className="pt-4 border-t border-stone-600">
              <p className="text-stone-100 font-medium">
                オーナー様にとって大切な資産を、長期視点で守りながら育てていく。<br />
                <span className="text-amber-400">それが当社の運営スタイルです。</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyOverview;
